import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Typography,
  Select,
  Row,
  Col,
  Collapse,
  Tooltip,
  App,
} from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { resolveEntityId } from "../../../utils/route-entity-id";
import "./programForm.scss";
import {
  createProgram,
  updateProgram,
} from "../../../services/programs/programs.service";
import { DeleteOutlined, InfoCircleOutlined } from "@ant-design/icons";
import ActivityIndicator from "../../ActivityIndicator";
import { useDispatch } from "react-redux";
import { handleSubmitFormResponseManage } from "../../../utils";
import { initialValuesProgram } from "./utils";
import { ProgramEntityInput } from "../../../services/programs/programs.types";
import { usePrograms } from "../../../hooks/usePrograms";
import { getAllCourses } from "../../../services/courses/courses.service";
import { getAllProjectsMinimal } from "../../../services/projects/projects.service";
import { useGrades } from "../../../hooks/useGrades";
import { useSubjects } from "../../../hooks/useSubject";
import { useNotification } from "../../../hooks/useNotification";
import { CourseMinimalResponse } from "../../../types/api/getCourse";
import { ProjectMinimalResponse } from "../../../types/api/getProject";
import ProgramResourceSelector from "./components/ProgramResourceSelector";
import { parseWeekRange, weekRangesOverlap } from "../../../utils/weekRange";
import EntityHistory from "../../EntityHistory";
import SortableList from "../../SortableList";

const READING_COMPREHENSION_SUBJECT = "Comprensión Lectora";

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface CourseSelector {
  courseId?: number;
  pages: string;
  week: string;
  trimester: number;
  // Stable per-row id for drag-and-drop. Kept out of the submit payload; it
  // only needs to survive edits (which replace the row object) so the sortable
  // row does not remount and steal input focus mid-typing.
  _uid: string;
}

let courseRowSeq = 0;
const nextCourseRowUid = () => `course-row-${(courseRowSeq += 1)}`;

interface ProjectSelector {
  projectId?: number;
  // Free-text school-cycle week, same format as the courses ("2" or "2-3").
  // Kept as typed; trimmed and validated with parseWeekRange on Guardar.
  week: string;
  /**
   * 1-based display position. Typed nullable because the column is an editable
   * InputNumber: clearing it really does put `null` in state, and the submit
   * validation is what turns that back into a hard requirement. Pretending it
   * is always a number here would just move the lie one layer down.
   */
  order_by: number | null;
  trimester: number;
  // Stable per-row id. Kept out of the submit payload; it exists so editing the
  // numeric Orden field (which replaces the row object) does not remount the
  // row and steal focus mid-typing — the same reason the course rows carry one.
  _uid: string;
}

let projectRowSeq = 0;
const nextProjectRowUid = () => `project-row-${(projectRowSeq += 1)}`;

const PROJECTS_SUBJECT = "Proyectos";

// Copy approved in the ticket mock. The "Sobre el orden" card and the column
// header tooltips share these exact strings on purpose: the tooltip is the
// same explanation in a smaller place, not a second wording to keep in sync.
const PROJECT_ORDER_HELP =
  "El campo Orden define la secuencia en que se mostrarán los proyectos en la plataforma.";
const PROJECT_UNIQUE_ORDER_HELP =
  "No debe haber dos proyectos con el mismo número de orden en la misma asignatura y grado.";
const PROJECT_WEEK_HELP =
  "Puedes cambiar la Semana sin que esto afecte el orden.";

/**
 * Column widths for the projects table, shared by the header and the rows so
 * the two cannot drift apart.
 *
 * These total 20 of antd's 24, not 24. `.courseHeaders` and `.courseDetails`
 * set `display: flex; gap: 10px` in programForm.scss, so each row also spends
 * 10px per gutter ON TOP of the percentage the spans claim: five columns cost
 * `spans/24 × 100% + 40px`. A full 24 is 100% + 40px, which overflows and wraps
 * the last column onto its own line — and this table sits inside a two-thirds
 * Col, where those absolute 40px eat an even bigger share. 20 is the total the
 * courses header has always used here.
 *
 * The Select and Input columns stay at 4 or more because that same SCSS gives
 * `.ant-select` and `.ant-input` a `min-width: 120px`; at the narrow breakpoint
 * span 3 is about 95px, so the field would spill into the gap. Orden is an
 * InputNumber at `width: 100%` with no such floor, so it can take 3.
 */
const PROJECT_GRID_SPANS = {
  // Widest: shows "Proyecto 113 - Lotería del cuerpo en movimiento".
  project: 6,
  // Free-text range such as "11-13"; the 120px input floor is what sizes it.
  week: 4,
  order: 3,
  trimester: 4,
  // Holds one icon button, but the "Acciones" header label is what sets the
  // floor: at span 2 the column gave the word a 56px box, it wrapped onto a
  // second line, and centring the taller cell lifted it 12px out of line with
  // the other four. Measured at 1600px wide.
  actions: 3,
} as const;

/**
 * Info affordance for a projects-table column header.
 *
 * The sentence is both the tooltip and the button's accessible name: a tooltip
 * is only reachable by hover, which a screen reader user does not have, so an
 * icon whose whole job is to carry that sentence has to expose it as its name.
 * A Button rather than a bare icon so it is keyboard-focusable — the repo's
 * Tooltip pattern (Layout/Header) is the same shape.
 */
const ColumnInfo: React.FC<{ help: string }> = ({ help }) => (
  <Tooltip title={help}>
    <Button
      type="text"
      size="small"
      aria-label={help}
      icon={<InfoCircleOutlined />}
    />
  </Tooltip>
);

// Ascending by display position. Used on load and on save only — never while
// the user is typing, which would yank the row out from under the cursor.
const byOrderBy = (
  a: { order_by: number | null },
  b: { order_by: number | null }
) => (a.order_by ?? 0) - (b.order_by ?? 0);

const ProgramForm: React.FC = () => {
  const { notification } = App.useApp();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Synchronous re-entrancy guard: `loading` lags a render behind, so a fast
  // double click on Guardar would fire two create/update calls.
  const submitInFlightRef = useRef(false);
  const { getProgramById } = usePrograms();
  const { subjects, loading: loadingSubjects } = useSubjects();
  const { grades, loading: loadingGrades } = useGrades();
  const { id: idParam } = useParams();
  // URL param first so deep links (content map, refresh, shared URLs) work;
  // navigation state is the legacy fallback used by the programs list.
  const programId = resolveEntityId(idParam, location?.state);
  const [initialValues, setInitialValues] =
    useState<ProgramEntityInput>(initialValuesProgram);

  const [courseDetails, setCourseDetails] = useState<CourseSelector[]>([]);
  const [projectDetails, setProjectDetails] = useState<ProjectSelector[]>([]);
  const [availableCourses, setAvailableCourses] = useState<
    CourseMinimalResponse[]
  >([]);
  const [availableProjects, setAvailableProjects] = useState<
    ProjectMinimalResponse[]
  >([]);
  const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
  const [loadingProjects, setLoadingProjects] = useState<boolean>(false);
  // Project weeks are only flagged after a Guardar they blocked: a new row
  // starts empty, and painting it red before the user typed would be noise.
  // From then on the flag follows the value, so fixing a week clears it.
  const [showProjectWeekErrors, setShowProjectWeekErrors] =
    useState<boolean>(false);

  const fetchInfo = async () => {
    setLoading(true);
    try {
      // Cargar todos los cursos disponibles
      setLoadingCourses(true);
      try {
        const coursesData = await getAllCourses();
        setAvailableCourses(coursesData || []);
      } catch (coursesError) {
        console.error("Error loading courses:", coursesError);
        notification.error({
          message: "Error",
          description: "No se pudieron cargar los cursos disponibles.",
          placement: "top",
          duration: 3,
        });
        setAvailableCourses([]);
      }
      setLoadingCourses(false);

      // Cargar todos los proyectos disponibles
      setLoadingProjects(true);
      try {
        const projectsData = await getAllProjectsMinimal();
        setAvailableProjects(projectsData || []);
      } catch (projectsError) {
        console.error("Error loading projects:", projectsError);
        notification.error({
          message: "Error",
          description: "No se pudieron cargar los proyectos disponibles.",
          placement: "top",
          duration: 3,
        });
        setAvailableProjects([]);
      }
      setLoadingProjects(false);

      if (programId) {
        const response = await getProgramById(programId);
        // Services are being migrated to throw on error instead of
        // returning null — treat a falsy result as a failed load either way.
        if (!response) {
          throw new Error("No se pudo cargar el programa.");
        }
        setInitialValues({
          courses: response.courses ? response.courses : [],
          description: response.description,
          program_code: response.program_code || "",
          subject_id: response.subject_id,
          title: response.title,
          grade_id: response.grade_id,
        });

        if (response.courses) {
          setCourseDetails(
            response.courses.map((course) => ({
              courseId: course.id,
              ...course,
              _uid: nextCourseRowUid(),
            }))
          );
        }

        if (response.projects) {
          setProjectDetails(
            response.projects
              .map((project, index) => ({
                projectId: project.id,
                // The backend already falls back to the project's own weeks
                // when the program row has none; a row with neither loads
                // empty and the Guardar validation asks for it.
                week: project.week ?? "",
                // Rows stored before order_by existed come back with 0 or
                // nothing: fall back to the incoming position so the column
                // never shows a 0 or a blank. A partially migrated program can
                // end up with a collision that way, which is exactly what the
                // duplicate check surfaces to the user.
                order_by: project.order_by || index + 1,
                trimester: project.trimester,
                _uid: nextProjectRowUid(),
              }))
              .sort(byOrderBy)
          );
        }
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      setLoadingCourses(false);
      setLoadFailed(true);
      console.error(error);
      const backendMessage = error?.response?.data?.message;
      notification.error({
        message: "Error",
        description: backendMessage
          ? `No se pudo cargar la información. Reintentá o volvé al listado.\n${backendMessage}`
          : "No se pudo cargar la información. Reintentá o volvé al listado.",
        placement: "top",
        duration: 3,
      });
    }
  };

  useEffect(() => {
    fetchInfo();
  }, [programId]);

  const handleDetailChange = (index: number, field: string, value: any) => {
    const updatedDetails = [...courseDetails];
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    setCourseDetails(updatedDetails);
  };

  const handleRemoveCourse = (index: number) => {
    setCourseDetails(courseDetails.filter((_, i) => i !== index));
  };

  const handleReorderCourses = (reordered: CourseSelector[]) => {
    // The submit persists order_by from the array index, so reordering the
    // array is the whole change — no extra request, no autosave.
    setCourseDetails(reordered);
  };

  const handleProjectDetailChange = (
    index: number,
    field: string,
    value: any
  ) => {
    const updatedDetails = [...projectDetails];
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    setProjectDetails(updatedDetails);
  };

  const handleRemoveProject = (index: number) => {
    setProjectDetails(projectDetails.filter((_, i) => i !== index));
  };

  // Order numbers claimed by more than one row. Drives both the per-row error
  // state and the submit-blocking message, so the two can never disagree.
  const duplicatedProjectOrders = useMemo(() => {
    const counts = new Map<number, number>();
    projectDetails.forEach((project) => {
      if (typeof project.order_by === "number") {
        counts.set(project.order_by, (counts.get(project.order_by) ?? 0) + 1);
      }
    });
    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([order]) => order)
      .sort((a, b) => a - b);
  }, [projectDetails]);

  const isDuplicatedProjectOrder = (orderBy: number | null) =>
    typeof orderBy === "number" &&
    duplicatedProjectOrders.indexOf(orderBy) !== -1;

  // Same format as the courses ("N" or "N-M", N <= M, required), checked on
  // the trimmed value because that is what Guardar sends.
  const isValidProjectWeek = (week: string) => !!parseWeekRange(week.trim());

  const isInvalidProjectWeek = (week: string) =>
    showProjectWeekErrors && !isValidProjectWeek(week);

  const handleSubmit = async () => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      await submitProgram();
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const submitProgram = async () => {
    const isProject = isProjectSubject();

    // Validaciones para cursos
    if (
      !isProject &&
      courseDetails.some((course) => typeof course.courseId !== "number")
    ) {
      notify(
        "error",
        "Error en Cursos",
        "Todas las filas deben tener un curso seleccionado."
      );
      return;
    }

    // Validaciones para proyectos
    if (
      isProject &&
      projectDetails.some((project) => typeof project.projectId !== "number")
    ) {
      notify(
        "error",
        "Error en Proyectos",
        "Todas las filas deben tener un proyecto seleccionado."
      );
      return;
    }

    // El orden es el único criterio de presentación en la plataforma, así que
    // tiene que estar completo y ser único antes de guardar.
    if (isProject) {
      const hasInvalidOrder = projectDetails.some(
        (project) =>
          typeof project.order_by !== "number" ||
          !Number.isInteger(project.order_by) ||
          project.order_by < 1
      );
      if (hasInvalidOrder) {
        notify(
          "error",
          "Orden inválido",
          "Cada proyecto debe tener un número de orden entero mayor o igual a 1."
        );
        return;
      }

      if (duplicatedProjectOrders.length > 0) {
        notify(
          "error",
          "Orden duplicado",
          `Hay más de un proyecto con el orden ${duplicatedProjectOrders.join(
            ", "
          )}. Cada proyecto debe tener un número de orden distinto.`
        );
        return;
      }
    }

    if (!initialValues.grade_id) {
      notify(
        "error",
        "Error en Grado",
        "Todo programa debe tener un grado para asignarle a los cursos."
      );
      return;
    }

    // Validación de formato de semanas para proyectos. Mismo formato y mismo
    // mensaje que los cursos, pero SIN chequeo de solapamiento: la duración de
    // un proyecto es libre y el orden en plataforma lo da el campo Orden.
    if (isProject) {
      const hasInvalidWeek = projectDetails.some(
        (project) => !isValidProjectWeek(project.week)
      );
      if (hasInvalidWeek) {
        setShowProjectWeekErrors(true);
        notify(
          "error",
          "Formato de semana inválido",
          'El formato de semana debe ser "N" o "N-M" (ej: "2" o "2-3").'
        );
        return;
      }
    }

    // Validación de formato y solapamiento de semanas para cursos
    if (!isProject) {
      const hasInvalidFormat = courseDetails.some(
        (course) => !parseWeekRange(course.week)
      );
      if (hasInvalidFormat) {
        notify(
          "error",
          "Formato de semana inválido",
          'El formato de semana debe ser "N" o "N-M" (ej: "2" o "2-3").'
        );
        return;
      }

      const hasOverlap = courseDetails.some((course, idx) =>
        courseDetails.some(
          (other, otherIdx) =>
            idx !== otherIdx && weekRangesOverlap(course.week, other.week)
        )
      );
      if (hasOverlap) {
        notify(
          "error",
          "Semanas solapadas",
          "No se pueden guardar cursos con semanas que se solapan."
        );
        return;
      }
    }

    setLoading(true);
    const programInput: ProgramEntityInput = {
      title: initialValues.title,
      description: initialValues.description,
      program_code: initialValues.program_code,
      grade_id: initialValues.grade_id,
      subject_id: initialValues.subject_id,
      courses: isProject
        ? []
        : courseDetails
            .filter((course) => course.courseId)
            .map((course) => ({
              id: course.courseId ? course.courseId : -1,
              pages: course.pages,
              trimester: course.trimester,
              week: course.week,
              grade_id: initialValues.grade_id!,
            })),
      projects: isProject
        ? projectDetails
            .filter((project) => project.projectId)
            .map((project) => ({
              id: project.projectId ? project.projectId : -1,
              // Validated above: by here every row holds a valid week.
              week: project.week.trim(),
              // Validated above: by here every row holds an integer >= 1.
              order_by: project.order_by as number,
              trimester: project.trimester,
            }))
            // `month` (SCRUM-824) is no longer sent, and `_uid` never leaves
            // the form.
            .sort(byOrderBy)
        : undefined,
    };

    try {
      await handleSubmitFormResponseManage(
        programInput,
        createProgram,
        updateProgram,
        navigate,
        dispatch,
        "program",
        "addProgram",
        "updateProgram",
        programId
      );
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      notification.error({
        message: "Error",
        description: "Ocurrió un error al guardar el programa.",
        placement: "top",
      });
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleAddCourseAtIndex = (index: number) => {
    const newCourse = {
      courseId: undefined,
      pages: "",
      week: "1",
      trimester: 1,
      _uid: nextCourseRowUid(),
    };
    const updatedDetails = [
      ...courseDetails.slice(0, index),
      newCourse,
      ...courseDetails.slice(index),
    ];
    setCourseDetails(updatedDetails);
  };

  const handleAddProjectAtIndex = (index: number) => {
    // Next AVAILABLE number, not the row count: a program whose orders are
    // 1 and 5 must offer 6, and deleting a middle row must not hand out a
    // number another row already owns.
    const nextOrderBy =
      projectDetails.reduce(
        (highest, project) => Math.max(highest, project.order_by ?? 0),
        0
      ) + 1;
    const newProject: ProjectSelector = {
      projectId: undefined,
      week: "",
      order_by: nextOrderBy,
      trimester: 1,
      _uid: nextProjectRowUid(),
    };
    const updatedDetails = [
      ...projectDetails.slice(0, index),
      newProject,
      ...projectDetails.slice(index),
    ];
    setProjectDetails(updatedDetails);
  };

  const isProjectSubject = () => {
    const projectSubject = subjects.find((s) => s.name === PROJECTS_SUBJECT);
    return projectSubject && initialValues.subject_id === projectSubject.id;
  };

  const isReadingComprehensionSubject = () => {
    const subject = subjects.find(
      (s) => s.name === READING_COMPREHENSION_SUBJECT
    );
    return subject && initialValues.subject_id === subject.id;
  };

  if (loading || loadingCourses || loadingGrades || loadingSubjects) {
    return <ActivityIndicator />;
  }

  return (
    <div className="programFormContainer">
      <Title level={3}>
        {programId ? "Editar Programa" : "Crear Programa"}
      </Title>

      <Form
        initialValues={initialValues}
        layout="vertical"
        onFinish={handleSubmit}
        className="programForm"
      >
        <Title level={4}>Información General</Title>

        <Form.Item
          label="Título"
          name="title"
          rules={[
            { required: true, message: "El título del programa es requerido" },
          ]}
        >
          <Input
            value={initialValues.title}
            onChange={(e) =>
              setInitialValues({ ...initialValues, title: e.target.value })
            }
          />
        </Form.Item>

        <Form.Item
          label="Descripción"
          name="description"
          rules={[
            {
              required: true,
              message: "La descripción del programa es requerida",
            },
          ]}
        >
          <Input
            value={initialValues.description}
            onChange={(e) =>
              setInitialValues({
                ...initialValues,
                description: e.target.value,
              })
            }
          />
        </Form.Item>

        <Form.Item
          label="Código del Programa"
          name="program_code"
          rules={[
            {
              required: true,
              message: "El código del programa es requerido",
            },
          ]}
          help={
            programId
              ? "Contactate con tecnología para hacer este cambio"
              : undefined
          }
          tooltip={
            programId
              ? undefined
              : "Este código identifica al programa y se usa para vincularlo con cursos y clases en todo el sistema."
          }
        >
          <Input
            placeholder="Ingrese el código del programa"
            value={initialValues.program_code}
            onChange={(e) =>
              setInitialValues({
                ...initialValues,
                program_code: e.target.value,
              })
            }
            disabled={!!programId}
          />
        </Form.Item>

        <Form.Item
          label="Asignatura"
          name="subject_id"
          rules={[{ required: true, message: "La asignatura es requerida" }]}
        >
          <Select
            value={initialValues.subject_id}
            onChange={(value: number) =>
              setInitialValues({
                ...initialValues,
                subject_id: value,
              })
            }
          >
            {subjects.map((subject) => (
              <Option key={subject.id} value={subject.id}>
                {subject.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Grado"
          name="grade_id"
          rules={[
            { required: true, message: "El grado del programa es requerido" },
          ]}
        >
          <Select
            placeholder="Grado"
            value={initialValues.grade_id}
            onChange={(value: number) =>
              setInitialValues({
                ...initialValues,
                grade_id: value,
              })
            }
          >
            {grades.map((grade) => (
              <Option key={grade.id} value={grade.id}>
                {grade.grade}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Title level={4}>{isProjectSubject() ? "Proyectos" : "Cursos"}</Title>

        {isProjectSubject() && (
          <>
            <Paragraph type="secondary" style={{ marginBottom: 8 }}>
              Define los proyectos que se trabajarán durante el ciclo escolar,
              la semana en que corresponde cada uno y su orden.
            </Paragraph>
            <Alert
              type="info"
              showIcon
              message="Los proyectos se mostrarán siempre según el campo Orden."
            />
          </>
        )}

        {!isProjectSubject() && courseDetails.length > 0 && (
          <Row
            className="courseHeaders"
            gutter={16}
            style={{ paddingLeft: 30 }}
          >
            <Col span={6}>
              <Title level={5}>Curso</Title>
            </Col>
            <Col span={4}>
              <Title level={5}>Semana</Title>
            </Col>
            <Col span={4}>
              <Title level={5}>Páginas</Title>
            </Col>
            <Col span={4}>
              <Title level={5}>Trimestre</Title>
            </Col>
            <Col span={2}></Col>
          </Row>
        )}

        {!isProjectSubject() && (
          <SortableList
            items={courseDetails}
            onReorder={handleReorderCourses}
            getId={(course) => course._uid}
            renderItem={(course, index, { dragHandle }) => (
              <div
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                {dragHandle}
                <Row
                  className="courseDetails"
                  align="middle"
                  gutter={16}
                  style={{ flex: 1 }}
                >
                <Col span={6}>
                  <Select
                    placeholder="Selecciona un curso"
                    value={courseDetails[index].courseId || undefined}
                    onChange={(value) =>
                      handleDetailChange(index, "courseId", value)
                    }
                    style={{ width: "100%" }}
                    allowClear
                    onClear={() => handleDetailChange(index, "courseId", null)}
                  >
                    {availableCourses
                      .filter(
                        (course) =>
                          !courseDetails.some(
                            (detail) =>
                              detail.courseId === course.id &&
                              detail.courseId !== courseDetails[index].courseId
                          )
                      )
                      .sort((a, b) => b.id - a.id) // Ordenar descendente por ID
                      .map((course) => (
                        <Option key={course.id} value={course.id}>
                          {course.id} - {course.title}
                        </Option>
                      ))}
                  </Select>
                </Col>
                <Col span={4}>
                  <Input
                    placeholder="Ej: 2 o 3-4"
                    value={course.week}
                    onChange={(e) =>
                      handleDetailChange(index, "week", e.target.value)
                    }
                  />
                </Col>
                <Col span={4}>
                  <Input
                    placeholder="Páginas"
                    value={course.pages}
                    onChange={(e) =>
                      handleDetailChange(index, "pages", e.target.value)
                    }
                  />
                </Col>
                <Col span={4}>
                  <Select
                    placeholder="Trimestre"
                    value={course.trimester}
                    onChange={(value) =>
                      handleDetailChange(index, "trimester", value)
                    }
                  >
                    <Option value={1}>1</Option>
                    <Option value={2}>2</Option>
                    <Option value={3}>3</Option>
                  </Select>
                </Col>
                <Col span={2}>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveCourse(index)}
                  />
                </Col>
                <Col span={2}>
                  <Button
                    type="dashed"
                    onClick={() => handleAddCourseAtIndex(index + 1)}
                  >
                    + Agregar Abajo
                  </Button>
                </Col>
                </Row>
              </div>
            )}
          />
        )}

        {/* Selector de Proyectos. La tabla y la ayuda comparten una Row para
            que la tarjeta quede al costado en escritorio y se apile debajo en
            pantallas angostas. */}
        {isProjectSubject() && (
          <Row gutter={16}>
            <Col span={24} lg={16}>
              {projectDetails.length > 0 && (
                // align="middle" + marginBottom:0 on every header title so the
                // two columns carrying an info icon stay on the same line as
                // the three that do not.
                //
                // Spans total PROJECT_GRID_SPANS worth of the 24-column grid,
                // NOT 24 — see the constant for the gap arithmetic.
                <Row
                  className="courseHeaders projectHeaders"
                  gutter={16}
                  align="middle"
                >
                  <Col span={PROJECT_GRID_SPANS.project}>
                    <Title level={5} style={{ marginBottom: 0 }}>
                      Proyecto
                    </Title>
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.week}>
                    <div className="projectHeaderCell">
                      <Title level={5} style={{ marginBottom: 0 }}>
                        Semana
                      </Title>
                      <ColumnInfo help={PROJECT_WEEK_HELP} />
                    </div>
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.order}>
                    <div className="projectHeaderCell">
                      <Title level={5} style={{ marginBottom: 0 }}>
                        Orden
                      </Title>
                      <ColumnInfo help={PROJECT_ORDER_HELP} />
                    </div>
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.trimester}>
                    <Title level={5} style={{ marginBottom: 0 }}>
                      Trimestre
                    </Title>
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.actions}>
                    <Title level={5} style={{ marginBottom: 0 }}>
                      Acciones
                    </Title>
                  </Col>
                </Row>
              )}

              {projectDetails.map((project, index) => (
                <Row
                  className="courseDetails"
                  align="middle"
                  gutter={16}
                  key={project._uid}
                >
                  <Col span={PROJECT_GRID_SPANS.project}>
                    <Select
                      placeholder="Selecciona un proyecto"
                      aria-label={`Proyecto ${index + 1}`}
                      value={project.projectId || undefined}
                      onChange={(value) =>
                        handleProjectDetailChange(index, "projectId", value)
                      }
                      style={{ width: "100%" }}
                      allowClear
                      onClear={() =>
                        handleProjectDetailChange(index, "projectId", null)
                      }
                    >
                      {availableProjects
                        .filter(
                          (available) =>
                            !projectDetails.some(
                              (detail) =>
                                detail.projectId === available.id &&
                                detail.projectId !== project.projectId
                            )
                        )
                        .sort((a, b) => b.id - a.id) // Ordenar descendente por ID
                        .map((available) => (
                          <Option key={available.id} value={available.id}>
                            Proyecto {available.id} - {available.title}
                          </Option>
                        ))}
                    </Select>
                  </Col>
                  {/* Semana y Orden se quedan FUERA de un Form.Item con `name`:
                      rc-field-form inyectaría su propio valor y pisaría el
                      controlado (gotcha conocido del repo). Los controla
                      projectDetails, igual que el Trimestre de al lado. */}
                  <Col span={PROJECT_GRID_SPANS.week}>
                    <Input
                      placeholder="Semana"
                      // La columna se repite por fila bajo un encabezado
                      // compartido, y un <Title> de sección no entra en el
                      // nombre accesible: sin esto todas suenan igual.
                      aria-label={`Semana del proyecto ${index + 1}`}
                      value={project.week}
                      onChange={(e) =>
                        handleProjectDetailChange(index, "week", e.target.value)
                      }
                      status={
                        isInvalidProjectWeek(project.week) ? "error" : undefined
                      }
                      // Same reason as Orden: the red border alone is colour
                      // only, so the row also announces itself as invalid.
                      aria-invalid={isInvalidProjectWeek(project.week)}
                    />
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.order}>
                    <InputNumber
                      min={1}
                      aria-label={`Orden del proyecto ${index + 1}`}
                      value={project.order_by}
                      onChange={(value) =>
                        handleProjectDetailChange(index, "order_by", value)
                      }
                      status={
                        isDuplicatedProjectOrder(project.order_by)
                          ? "error"
                          : undefined
                      }
                      // antd's `status` only paints a red border, so the clash
                      // would be signalled by colour alone. The blocking
                      // message on Guardar names the number; this makes the
                      // offending row itself reachable by assistive tech.
                      aria-invalid={isDuplicatedProjectOrder(project.order_by)}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.trimester}>
                    <Select
                      placeholder="Trimestre"
                      aria-label={`Trimestre del proyecto ${index + 1}`}
                      value={project.trimester}
                      onChange={(value) =>
                        handleProjectDetailChange(index, "trimester", value)
                      }
                    >
                      <Option value={1}>1</Option>
                      <Option value={2}>2</Option>
                      <Option value={3}>3</Option>
                    </Select>
                  </Col>
                  <Col span={PROJECT_GRID_SPANS.actions}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      aria-label={`Eliminar el proyecto ${index + 1}`}
                      onClick={() => handleRemoveProject(index)}
                    />
                  </Col>
                </Row>
              ))}

              <Button
                type="dashed"
                onClick={() => handleAddProjectAtIndex(projectDetails.length)}
                block
                style={{ marginTop: 8 }}
              >
                + Agregar Proyecto
              </Button>
            </Col>

            <Col span={24} lg={8}>
              <Card title="Sobre el orden" size="small">
                <Paragraph>{PROJECT_ORDER_HELP}</Paragraph>
                <Paragraph>{PROJECT_UNIQUE_ORDER_HELP}</Paragraph>
                <Paragraph style={{ marginBottom: 0 }}>
                  {PROJECT_WEEK_HELP}
                </Paragraph>
              </Card>
            </Col>
          </Row>
        )}

        {/* Botón para agregar un curso al final */}
        {!isProjectSubject() && (
          <Button
            type="dashed"
            onClick={() => handleAddCourseAtIndex(courseDetails.length)}
            block
          >
            + Agregar Curso
          </Button>
        )}

        {/* Lecturas del programa — solo para Comprensión Lectora ya creado.
            El vínculo se gestiona aparte del guardado del programa. */}
        {isReadingComprehensionSubject() &&
          (programId ? (
            <ProgramResourceSelector programId={programId} />
          ) : (
            <p style={{ color: "#8c8c8c", marginTop: 16 }}>
              Guarda el programa primero para poder asociarle lecturas.
            </p>
          ))}

        <Form.Item>
          <div className="formButtons">
            <Button htmlType="button" onClick={handleCancel}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" disabled={loadFailed}>
              Guardar
            </Button>
          </div>
        </Form.Item>
      </Form>

      {programId && (
        <Collapse
          items={[
            {
              key: "history",
              label: "Historial de cambios",
              children: (
                <EntityHistory entityType="programs" entityId={programId} />
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default ProgramForm;
